import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { normalizeContentfulAssetUrl } from '@garnish/forms';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import type { Asset } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the Contentful entry backing the bundle screen, falling back to
 * bundled copy for any text the entry leaves out. Banners fall back where
 * they're shown, since their copy needs the add to bag results.
 */
export const useBundleContent = () => {
  const { formatMessage } = useIntl();

  const { data, fetching } = useContentfulContentTypeEntry<BundleFields>({
    contentType: BUNDLE_CONTENT_TYPE,
  });

  const fields = data?.fields;

  // ─── Derived ─────────────────────────────────────────────────────────

  const content = useMemo<BundleContent>(() => {
    const imageUrl = fields?.image?.fields.file?.url;
    const entryPointImageUrl = fields?.entryPointImage?.fields.file?.url;

    return {
      name: fields?.name ?? formatMessage(messages.name),
      description: fields?.description ?? formatMessage(messages.description),
      selectTitle: fields?.selectTitle ?? formatMessage(messages.selectTitle),
      selectLocationTitle:
        fields?.selectLocationTitle ?? formatMessage(messages.selectLocationTitle),
      selectLocationDescription:
        fields?.selectLocationDescription ?? formatMessage(messages.selectLocationDescription),
      selectLocationCta: fields?.selectLocationCta ?? formatMessage(messages.selectLocationCta),
      lineageKeys: fields?.productsLineageKeys ?? [],
      successBanner: fields?.successBanner,
      partialSuccessBanner: fields?.partialSuccessBanner,
      failureBanner: fields?.failureBanner,
      imageUrl: imageUrl ? normalizeContentfulAssetUrl(imageUrl) : undefined,
      imageA11y: fields?.image?.fields.description,
      entryPointCategoryIds: fields?.entryPointCategoryIds ?? [],
      entryPointImageUrl: entryPointImageUrl
        ? normalizeContentfulAssetUrl(entryPointImageUrl)
        : undefined,
      entryPointImageA11y: fields?.entryPointImage?.fields.description,
      entryPointProtein: fields?.entryPointProtein,
      entryPointCalories: fields?.entryPointCalories,
      entryPointPrice: fields?.entryPointPrice,
      entryPointCta: fields?.entryPointCta ?? formatMessage(messages.entryPointCta),
    };
  }, [fields, formatMessage]);

  return { content, fetching };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const BUNDLE_CONTENT_TYPE = 'productsBundle';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  name: {
    defaultMessage: 'Family Meal',
    description: 'Bundle | Fallback content | Bundle name',
  },
  description: {
    defaultMessage: 'Choose four of our hearty plates. Customize them after you add to your bag.',
    description: 'Bundle | Fallback content | Bundle description',
  },
  selectTitle: {
    defaultMessage: 'Select a plate',
    description: 'Bundle | Fallback content | Select a product CTA title',
  },
  selectLocationTitle: {
    defaultMessage: 'Where are you ordering from?',
    description: 'Bundle | Fallback content | Select location title',
  },
  selectLocationDescription: {
    defaultMessage: 'Pick a location to see what’s in this bundle.',
    description: 'Bundle | Fallback content | Select location description',
  },
  selectLocationCta: {
    defaultMessage: 'Select location',
    description: 'Bundle | Fallback content | Select location CTA',
  },
  entryPointCta: {
    defaultMessage: 'Start building',
    description: 'Bundle | Fallback content | Menu entry point CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BundleContent = {
  name: string;
  description: string;
  selectTitle: string;
  selectLocationTitle: string;
  selectLocationDescription: string;
  selectLocationCta: string;
  lineageKeys: string[];
  successBanner?: string;
  partialSuccessBanner?: string;
  failureBanner?: string;
  imageUrl?: string;
  imageA11y?: string;
  entryPointCategoryIds: string[];
  entryPointImageUrl?: string;
  entryPointImageA11y?: string;
  entryPointProtein?: string;
  entryPointCalories?: string;
  entryPointPrice?: string;
  entryPointCta: string;
};

type BundleFields = {
  name?: string;
  description?: string;
  selectTitle?: string;
  selectLocationTitle?: string;
  selectLocationDescription?: string;
  selectLocationCta?: string;
  productsLineageKeys?: string[];
  successBanner?: string;
  partialSuccessBanner?: string;
  failureBanner?: string;
  image?: Asset;
  entryPointCategoryIds?: string[];
  entryPointImage?: Asset;
  entryPointProtein?: string;
  entryPointCalories?: string;
  entryPointPrice?: string;
  entryPointCta?: string;
};
